import type { HTMLAttributes, ImgHTMLAttributes, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import clsx from 'clsx';

import personPlaceholder from './assets/person-placeholder.svg';
import teamPlaceholder from './assets/team-placeholder.svg';
import styles from './styles/Avatar.module.scss';

export type Team = {
  logo?: string;
  name?: string;
};

export type Player = {
  image: string;
  name: string;
};

type AvatarSize = 'xsmall' | 'small' | 'medium' | 'large';

const Avatar = ({
  children,
  className,
  size = 'medium',
  style,
  ...props
}: { size?: AvatarSize } & PropsWithChildren<HTMLAttributes<HTMLSpanElement>>) => (
  <span className={clsx(styles.avatar, styles[`${size}`], className)} data-cxy-name="avatar" style={style} {...props}>
    {children}
  </span>
);

const AvatarImage = ({ alt, className, src, ...props }: ImgHTMLAttributes<HTMLImageElement>) => {
  const [errorCount, setErrorCount] = useState(0);

  useEffect(() => {
    setErrorCount(0);
  }, [src]);

  const fallbackSrc = src?.includes('.png')
    ? src.replace('.png', '.svg')
    : src?.includes('.svg')
      ? src.replace('.svg', '.png')
      : undefined;
  const imageAlt = alt || 'Team placeholder image';

  const handleError = () => {
    setErrorCount(prev => prev + 1);
  };

  let currentSrc = src || teamPlaceholder;
  if (errorCount === 1 && fallbackSrc) {
    currentSrc = fallbackSrc;
  } else if (errorCount > 0) {
    currentSrc = teamPlaceholder;
  }

  return (
    <img
      alt={imageAlt}
      className={clsx(styles.avatarImage, { [styles.placeholder]: !src }, className)}
      onError={handleError}
      src={currentSrc}
      {...props}
    />
  );
};

export type TeamAvatarsProps = {
  awayTeam?: Team;
  homeTeam?: Team;
  isFlipped?: boolean;
  size?: AvatarSize;
};

const TeamAvatars = ({ awayTeam, homeTeam, isFlipped = false, size = 'medium' }: TeamAvatarsProps) => {
  const placeholderAlt = 'Team placeholder image';
  const awayTeamWithDefaults = {
    name: awayTeam?.name || placeholderAlt,
    logo: awayTeam?.logo || teamPlaceholder,
  };
  const homeTeamWithDefaults = {
    name: homeTeam?.name || placeholderAlt,
    logo: homeTeam?.logo || teamPlaceholder,
  };

  const away = isFlipped ? homeTeamWithDefaults : awayTeamWithDefaults;
  const home = isFlipped ? awayTeamWithDefaults : homeTeamWithDefaults;

  return (
    <div className={styles.teamAvatars}>
      <Avatar size={size}>
        <AvatarImage alt={`${home.name} logo`} className={styles.teamLogoImage} src={home.logo} />
      </Avatar>
      <Avatar size={size}>
        <AvatarImage alt={`${away.name} logo`} className={styles.teamLogoImage} src={away.logo} />
      </Avatar>
    </div>
  );
};

export type PersonAvatarsProps = {
  player: Player;
  size?: AvatarSize;
  team?: Team;
};

const PersonAvatar = ({ player, size = 'medium', team }: PersonAvatarsProps) => {
  const imageSrc = player.image || personPlaceholder;

  return (
    <div className={styles.personAvatar}>
      <Avatar size={size}>
        <AvatarImage alt={player.name} className={styles.personAvatarImage} src={imageSrc} />
      </Avatar>
      {team && (
        <div className={styles.playerAvatarTeamImage}>
          <img alt={team.name} src={team.logo} />
        </div>
      )}
    </div>
  );
};

const TeamAvatar = ({ size = 'medium', team }: { size?: AvatarSize; team?: Team }) => {
  const logoSrc = team?.logo || teamPlaceholder;
  const logoAlt = team?.name ? `${team.name} logo` : 'Team placeholder image';

  return (
    <Avatar size={size}>
      <AvatarImage alt={logoAlt} className={styles.teamLogoImage} src={logoSrc} />
    </Avatar>
  );
};

export { Avatar, AvatarImage, PersonAvatar, TeamAvatar, TeamAvatars };
